import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { mediaUrl } from "@/lib/api";
import { Heart, Eye, ArrowUpRight, Share2, ArrowLeft } from "lucide-react";
import ShareDialog from "@/components/ShareDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicVideo() {
  const { videoId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/public/videos/${videoId}`);
        setData(data);
      } catch (e) {
        setError(e?.response?.status === 404 ? "Video not found" : "Could not load video");
      }
    })();
  }, [videoId]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-[#f5f5f5] flex items-center justify-center grain">
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#ff4500] font-mono">── 404</div>
          <h1 className="font-display text-4xl mt-3 tracking-tighter">{error}</h1>
          <Link to="/" className="inline-block mt-6 px-6 py-3 bg-[#ff4500] hover:bg-[#ff5722] text-white text-sm">Go to Rasin 🇭🇹</Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-[#0a0a0a] text-[#a3a3a3] flex items-center justify-center text-sm tracking-[0.2em] uppercase">Loading</div>;
  }

  const { video, owner } = data;
  const shareUrl = `${window.location.origin}/v/${video.id}`;
  const ogUrl = `${BACKEND_URL}/api/og/v/${video.id}`;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#f5f5f5] grain">
      <header className="flex items-center justify-between px-6 sm:px-10 py-5 border-b border-[#2a2a2a]">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-9 h-9 flex flex-col overflow-hidden shadow-lg">
            <div className="flex-1 bg-[#00209F]" />
            <div className="flex-1 bg-[#D21034]" />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-display font-semibold tracking-tight text-lg">Rasin</span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3] mt-1">Haiti's Stage · 🇭🇹</span>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/login" className="px-4 py-2 text-xs uppercase tracking-[0.2em] text-[#a3a3a3] hover:text-[#f5f5f5]">Sign in</Link>
          <Link to="/register" className="px-5 py-2 text-xs uppercase tracking-[0.2em] bg-[#ff4500] hover:bg-[#ff5722] text-white">Join free</Link>
        </div>
      </header>

      <div className="px-6 sm:px-10 py-10 max-w-5xl mx-auto">
        {owner && (
          <Link to={`/u/${owner.handle}`} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#a3a3a3] hover:text-[#ff4500] mb-6">
            <ArrowLeft className="w-3 h-3" /> {owner.name}'s profile
          </Link>
        )}

        <div className="relative aspect-video bg-black border border-[#2a2a2a]">
          <video src={mediaUrl(video.video_url)} controls autoPlay playsInline className="w-full h-full" data-testid="public-video-player" poster={video.thumbnail_url ? mediaUrl(video.thumbnail_url) : undefined} />
        </div>

        <div className="mt-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 data-testid="public-video-title" className="font-display text-3xl sm:text-4xl tracking-tighter">{video.title}</h1>
            <div className="text-sm text-[#a3a3a3] mt-3 flex items-center gap-4">
              {owner && (
                <Link to={`/u/${owner.handle}`} className="hover:text-[#ff4500]">@{owner.handle}</Link>
              )}
              <span className="flex items-center gap-1 font-mono text-xs"><Eye className="w-3.5 h-3.5" />{video.views}</span>
              <span className="flex items-center gap-1 font-mono text-xs"><Heart className="w-3.5 h-3.5" />{video.like_count}</span>
            </div>
          </div>
          <button onClick={() => setShareOpen(true)} data-testid="public-share-video-btn" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1a1a1a] border border-[#2a2a2a] hover:border-[#ff4500] text-xs uppercase tracking-[0.2em]">
            <Share2 className="w-4 h-4" /> Share
          </button>
        </div>

        {video.description && (
          <p className="text-[#a3a3a3] mt-6 text-sm leading-relaxed whitespace-pre-line bg-[#141414] border border-[#2a2a2a] p-4">
            {video.description}
          </p>
        )}
      </div>

      <section className="px-6 sm:px-10 py-16 mt-10 border-t border-[#2a2a2a] bg-[#141414]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-4xl tracking-tighter">Watch more on Rasin 🇭🇹</h2>
          <p className="text-[#a3a3a3] mt-3 text-sm">Free to join. Upload your own videos, message {owner?.name || "creators"}, and more.</p>
          <Link to="/register" className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-[#ff4500] hover:bg-[#ff5722] text-white text-sm">
            Create your profile <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {shareOpen && (
        <ShareDialog
          title={`${video.title} · Rasin`}
          text={video.description || `Watch "${video.title}" on Rasin 🇭🇹`}
          shareUrl={shareUrl}
          ogUrl={ogUrl}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
}
